import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  createTextColumn,
  createEnumColumn,
  createDateColumn,
  useTableState,
  StatusBadge,
} from '@medprompt/shared';
import { StatusEnum } from '../enums/statusEnum';
import { ConsultationStatusEnum } from '../enums/consultationStatusEnum';
import { formatTimestamp } from './patientFormat';

const usePatientTables = () => {
  const location = useLocation();

  const {
    sortState: docsSortState,
    setSortState: setDocsSortState,
    filterValues: docsFilterValues,
    setFilterValues: setDocsFilterValues,
    disabledFilters: docsDisabledFilters,
    setDisabledFilters: setDocsDisabledFilters,
    paginationState: docsPaginationState,
    setPaginationState: setDocsPaginationState,
    highlightedRows,
    setHighlightedRows
  } = useTableState('PatientDocumentsTable', {
    defaultSortState: { columnKey: 'timestamp', direction: 'desc' }
  });

  useEffect(() => {
    const highlightDocumentId = location.state?.highlightDocumentId;
    if (highlightDocumentId) {
      setHighlightedRows(new Set([highlightDocumentId]));
      window.history.replaceState({}, document.title);
    }
  }, [location.state, setHighlightedRows]);

  const {
    sortState: consultsSortState,
    setSortState: setConsultsSortState,
    filterValues: consultsFilterValues,
    setFilterValues: setConsultsFilterValues,
    disabledFilters: consultsDisabledFilters,
    setDisabledFilters: setConsultsDisabledFilters,
    paginationState: consultsPaginationState,
    setPaginationState: setConsultsPaginationState,
    highlightedRows: highlightedConsultRows,
    setHighlightedRows: setHighlightedConsultRows
  } = useTableState('PatientConsultationsTable', {
    defaultSortState: { columnKey: 'timestamp', direction: 'desc' }
  });

  useEffect(() => {
    const highlightConsultationId = location.state?.highlightConsultationId;
    if (highlightConsultationId) {
      setHighlightedConsultRows(new Set([highlightConsultationId]));
      window.history.replaceState({}, document.title);
    }
  }, [location.state, setHighlightedConsultRows]);

  const {
    sortState: tasksSortState,
    setSortState: setTasksSortState,
    filterValues: tasksFilterValues,
    setFilterValues: setTasksFilterValues,
    disabledFilters: tasksDisabledFilters,
    setDisabledFilters: setTasksDisabledFilters,
    paginationState: tasksPaginationState,
    setPaginationState: setTasksPaginationState,
    highlightedRows: highlightedTaskRows,
    setHighlightedRows: setHighlightedTaskRows
  } = useTableState('PatientTasksTable', {
    defaultSortState: { columnKey: 'timestamp', direction: 'desc' }
  });

  const {
    sortState: refsSortState,
    setSortState: setRefsSortState,
    filterValues: refsFilterValues,
    setFilterValues: setRefsFilterValues,
    disabledFilters: refsDisabledFilters,
    setDisabledFilters: setRefsDisabledFilters,
    paginationState: refsPaginationState,
    setPaginationState: setRefsPaginationState,
    highlightedRows: highlightedRefRows,
    setHighlightedRows: setHighlightedRefRows
  } = useTableState('PatientReferralsTable', {
    defaultSortState: { columnKey: 'referredAt', direction: 'desc' }
  });

  const documentColumns = useMemo(() => [
    createTextColumn('title', 'Title', (d) => d.documentTitle || d.overrideTitle || 'Untitled', { sortable: true }),
    createEnumColumn(
      'status', 'Status',
      (d) => d.status,
      Object.keys(StatusEnum),
      {
        renderer: (status) => (
          <StatusBadge
            label={StatusEnum[status]?.description || status}
            colorFamily={StatusEnum[status]?.colorFamily || 'gray'}
            size="normal"
          />
        ),
        sortable: true
      }
    ),
    createDateColumn('timestamp', 'Date', (d) => d.timestamp, {
      renderer: (value) => formatTimestamp(value),
      compareFn: (a, b) => (a.timestamp || 0) - (b.timestamp || 0)
    })
  ], []);

  const consultationColumns = useMemo(() => [
    createEnumColumn(
      'status', 'Status',
      (c) => c.status,
      Object.keys(ConsultationStatusEnum),
      {
        renderer: (status) => (
          <StatusBadge
            label={ConsultationStatusEnum[status]?.description || status}
            colorFamily={ConsultationStatusEnum[status]?.colorFamily || 'gray'}
            size="normal"
          />
        ),
        sortable: true
      }
    ),
    createDateColumn('timestamp', 'Date', (c) => c.timestamp, {
      renderer: (value) => formatTimestamp(value),
      compareFn: (a, b) => (a.timestamp || 0) - (b.timestamp || 0)
    })
  ], []);

  const taskColumns = useMemo(() => [
    createTextColumn('text', 'Task', (t) => t.text || '', { sortable: true }),
    createEnumColumn(
      'status', 'Status',
      (t) => t.isComplete ? 'COMPLETE' : 'PENDING',
      ['PENDING', 'COMPLETE'],
      {
        renderer: (value) => (
          <StatusBadge
            label={value === 'COMPLETE' ? 'Complete' : 'Pending'}
            colorFamily={value === 'COMPLETE' ? 'green' : 'yellow'}
            size="normal"
          />
        ),
        sortable: true
      }
    ),
    createDateColumn('timestamp', 'Created', (t) => t.timestamp, {
      renderer: (value) => formatTimestamp(value),
      compareFn: (a, b) => (a.timestamp || 0) - (b.timestamp || 0)
    })
  ], []);

  const referralColumns = useMemo(() => [
    createTextColumn('text', 'Notes', (r) => r.text || '', { sortable: false }),
    createEnumColumn(
      'status', 'Status',
      (r) => r.isComplete ? 'COMPLETE' : 'PENDING',
      ['PENDING', 'COMPLETE'],
      {
        renderer: (value) => (
          <StatusBadge
            label={value === 'COMPLETE' ? 'Complete' : 'Pending'}
            colorFamily={value === 'COMPLETE' ? 'green' : 'yellow'}
            size="normal"
          />
        ),
        sortable: true
      }
    ),
    createDateColumn('referredAt', 'Date of Referral', (r) => r.referredAt, {
      renderer: (value) => formatTimestamp(value),
      compareFn: (a, b) => (a.referredAt || 0) - (b.referredAt || 0)
    }),
    createDateColumn('timestamp', 'Created at', (r) => r.timestamp, {
      renderer: (value) => formatTimestamp(value),
      compareFn: (a, b) => (a.timestamp || 0) - (b.timestamp || 0)
    })
  ], []);

  return {
    docsSortState,
    setDocsSortState,
    docsFilterValues,
    setDocsFilterValues,
    docsDisabledFilters,
    setDocsDisabledFilters,
    docsPaginationState,
    setDocsPaginationState,
    highlightedRows,
    setHighlightedRows,
    documentColumns,
    consultsSortState,
    setConsultsSortState,
    consultsFilterValues,
    setConsultsFilterValues,
    consultsDisabledFilters,
    setConsultsDisabledFilters,
    consultsPaginationState,
    setConsultsPaginationState,
    highlightedConsultRows,
    setHighlightedConsultRows,
    consultationColumns,
    tasksSortState,
    setTasksSortState,
    tasksFilterValues,
    setTasksFilterValues,
    tasksDisabledFilters,
    setTasksDisabledFilters,
    tasksPaginationState,
    setTasksPaginationState,
    highlightedTaskRows,
    setHighlightedTaskRows,
    taskColumns,
    refsSortState,
    setRefsSortState,
    refsFilterValues,
    setRefsFilterValues,
    refsDisabledFilters,
    setRefsDisabledFilters,
    refsPaginationState,
    setRefsPaginationState,
    highlightedRefRows,
    setHighlightedRefRows,
    referralColumns,
  };
};

export default usePatientTables;
